import React from 'react';

// Utility function to convert Clara OCR polygons to SVG paths
const polygonToSVGPath = (polygon) => {
  if (!polygon || polygon.length < 3) return '';
  
  const pathData = polygon.map((point, index) => {
    const command = index === 0 ? 'M' : 'L';
    return `${command} ${point[0]} ${point[1]}`;
  }).join(' ') + ' Z';
  
  return pathData;
};

// Generate room hotspot coordinates from polygon centroid
const getPolygonCentroid = (polygon) => {
  if (!polygon || polygon.length === 0) return { x: 0, y: 0 };
  
  const centroid = polygon.reduce((acc, point) => ({
    x: acc.x + point[0],
    y: acc.y + point[1]
  }), { x: 0, y: 0 });
  
  return {
    x: centroid.x / polygon.length,
    y: centroid.y / polygon.length
  };
};

// Calculate bounding box for SVG viewBox
const calculateBoundingBox = (roomPolygons) => {
  if (!roomPolygons || roomPolygons.length === 0) {
    return { minX: 0, minY: 0, maxX: 800, maxY: 600 };
  }
  
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  
  roomPolygons.forEach(room => {
    if (room.polygon) {
      room.polygon.forEach(point => {
        minX = Math.min(minX, point[0]);
        minY = Math.min(minY, point[1]);
        maxX = Math.max(maxX, point[0]);
        maxY = Math.max(maxY, point[1]);
      });
    }
  });
  
  // Add padding
  const padding = 50;
  return {
    minX: minX - padding,
    minY: minY - padding,
    maxX: maxX + padding,
    maxY: maxY + padding
  };
};

const FloorPlanSVGGenerator = ({ 
  claraOCRResult, 
  onRoomClick, 
  selectedRoom = null,
  className = "",
  style = {}
}) => {
  if (!claraOCRResult || !claraOCRResult.room_polygons) {
    return (
      <div className={`flex items-center justify-center bg-gray-100 rounded-lg ${className}`} style={style}>
        <p className="text-gray-500">Kein Floor Plan verfügbar</p>
      </div>
    );
  }
  
  const { room_polygons } = claraOCRResult;
  const boundingBox = calculateBoundingBox(room_polygons);
  const viewBoxWidth = boundingBox.maxX - boundingBox.minX;
  const viewBoxHeight = boundingBox.maxY - boundingBox.minY;
  
  return (
    <div className={`relative ${className}`} style={style}>
      <svg preserveAspectRatio="xMidYMid meet"
        viewBox={`${boundingBox.minX} ${boundingBox.minY} ${viewBoxWidth} ${viewBoxHeight}`}
        className="w-full h-full" 
        style={{ background: '#f8f9fa' }}
      >
        {/* Room polygons */}
        {room_polygons.map((room, index) => {
          const pathData = polygonToSVGPath(room.polygon);
          const isSelected = selectedRoom === index;
          const centroid = getPolygonCentroid(room.polygon);
          
          return (
            <g key={index}>
              {/* Room polygon */}
              <path
                d={pathData}
                fill={isSelected ? '#3b82f6' : '#e5e7eb'}
                stroke="#374151"
                strokeWidth="2"
                className="cursor-pointer transition-colors hover:fill-blue-200"
                onClick={() => onRoomClick && onRoomClick(room, index)}
              />
              
              {/* Room label */}
              {room.room_name && (
                <text
                  x={centroid.x}
                  y={centroid.y}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  className="fill-gray-700 text-sm font-medium pointer-events-none"
                  style={{ fontSize: '14px' }}
                >
                  {room.room_name}
                </text>
              )}
              
              {/* Area text */}
              {room.area_text && (
                <text
                  x={centroid.x}
                  y={centroid.y + 20}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  className="fill-gray-600 text-xs pointer-events-none"
                  style={{ fontSize: '12px' }}
                >
                  {room.area_text}
                </text>
              )}
              
              {/* Room number for unnamed rooms */}
              {!room.room_name && (
                <text
                  x={centroid.x}
                  y={centroid.y}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  className="fill-gray-600 text-sm font-medium pointer-events-none"
                  style={{ fontSize: '14px' }}
                >
                  Raum {index + 1}
                </text>
              )}
            </g>
          );
        })}
        
        {/* OCR text overlays */}
        {claraOCRResult.ocr_results && claraOCRResult.ocr_results.dimensions && 
         claraOCRResult.ocr_results.dimensions.map((dimension, index) => (
          <text
            key={`dim-${index}`}
            x={dimension.bbox[0]}
            y={dimension.bbox[1]}
            className="fill-red-600 text-xs font-mono pointer-events-none"
            style={{ fontSize: '10px' }}
          >
            {dimension.text}
          </text>
        ))}
      </svg>
      
      {/* Floor plan info overlay */}
      <div className="absolute top-4 left-4 bg-white bg-opacity-90 rounded-lg p-3 shadow-lg">
        <h4 className="font-medium text-gray-900 mb-1">Clara OCR Analyse</h4>
        <div className="text-sm text-gray-600 space-y-1">
          <div>🏠 {claraOCRResult.rooms_detected} Räume erkannt</div>
          <div>📝 {claraOCRResult.total_detections} Texte gefunden</div>
          <div>🎯 {claraOCRResult.rooms_with_names} benannte Räume</div>
        </div>
      </div>
      
      {/* Room selection info */}
      {selectedRoom !== null && room_polygons[selectedRoom] && (
        <div className="absolute bottom-4 left-4 bg-blue-600 text-white rounded-lg p-3 shadow-lg">
          <h5 className="font-medium mb-1">
            {room_polygons[selectedRoom].room_name || `Raum ${selectedRoom + 1}`}
          </h5>
          <div className="text-sm space-y-1">
            {room_polygons[selectedRoom].area_text && (
              <div>📐 {room_polygons[selectedRoom].area_text}</div>
            )}
            <div>📍 Polygon mit {room_polygons[selectedRoom].polygon?.length || 0} Punkten</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FloorPlanSVGGenerator;

